import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { dbService } from '../lib/supabase';
import {
  SPONSOR_INVITE_QUERY_PARAM,
  isSponsorInvitePreviewToken,
} from '../constants/sponsorInviteCampaign';

/**
 * Records invite link click then routes to signup with sponsor invite token (email not forced).
 * "Send test email" links carry a preview token: those render a safe preview here (no DB lookup,
 * no click/conversion tracking, no redirect into the real signup) so admins can verify the flow.
 */
const SponsorInviteLandingPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isPreview, setIsPreview] = useState(false);

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setError('This invitation link is invalid or incomplete.');
      return;
    }

    // Preview (test) link: valid by construction — show the preview state and stop.
    if (isSponsorInvitePreviewToken(token)) {
      setIsPreview(true);
      return;
    }

    let cancelled = false;

    const run = async () => {
      try {
        const invite = await dbService.getSponsorInviteByToken(token);
        if (!invite) {
          if (!cancelled) setError('This invitation link is invalid or has expired.');
          return;
        }
        if (invite.converted_user_id) {
          if (!cancelled) navigate('/startup-signup?mode=login&message=already_registered', { replace: true });
          return;
        }

        await dbService.recordSponsorInviteLinkClick(token);
        if (!cancelled) {
          navigate(`/startup-signup?${SPONSOR_INVITE_QUERY_PARAM}=${encodeURIComponent(token)}`, {
            replace: true,
          });
        }
      } catch (e) {
        console.error('SponsorInviteLandingPage:', e);
        if (!cancelled) setError('Unable to open this invitation. Please try again later.');
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate]);

  if (isPreview) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center px-4">
        <div className="max-w-md w-full rounded-2xl border border-electric-blue/20 bg-white shadow-sm p-6 text-center">
          <span className="inline-block mb-3 px-2.5 py-1 rounded-full bg-amber-100 text-amber-900 text-xs font-semibold">
            Preview · test invitation
          </span>
          <h1 className="text-lg font-bold text-charcoal mb-2">This invitation link is valid.</h1>
          <p className="text-sm text-slate-gray mb-5">
            This is a preview of the "View your invitation" link. Real recipients would be taken
            straight to sign up. No account, click, or conversion is recorded for a test.
          </p>
          <button
            type="button"
            onClick={() => navigate('/startup-signup')}
            className="px-5 py-2.5 rounded-xl bg-gradient-primary text-white text-sm font-semibold"
          >
            View the sign-up page
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center px-4">
        <p className="text-charcoal text-center max-w-md">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-[50vh] flex flex-col items-center justify-center px-4">
      <Loader2 className="w-10 h-10 text-electric-blue animate-spin mb-4" />
      <p className="text-slate-gray">Opening your invitation…</p>
    </div>
  );
};

export default SponsorInviteLandingPage;
